import React from 'react';
import styled from 'styled-components';
import { FaSignInAlt } from 'react-icons/fa';

import AccountStatus from './AccountStatus';
import useScreenQuery from '../../../hooks/useScreenQuery';
import { formatCurrency } from '../../../utils/formatCurrency';
import PlayupThemeProvider from '../../../themes/PlayupThemeProvider';
import Button from '../../../components/Button/Button';

/*
	DevNote: Components not migrated to storybook and why:
	GreenId: Very complicated to migrate to storybook
	ProfileForm: Very complicated to migrate to storybook
	ResetPasswordModal: This modal meant to be temporary, so no need to be refactored to storybook
	AvatarUploader: No design provided for the uploader, hold on refactoring
*/
const PortalProfile = ({
	isUS,
	user,
	refreshPortalUser,
	notify,
	handleDeposit,
	GreenId,
	ResetPasswordModal,
	ProfileForm,
	AvatarUploader,
}: PortalProfileProps) => {
	const { isSmall } = useScreenQuery();
	const [showVerifyID, setShowVerifyID] = React.useState(false);
	const [resetPasswordModal, setResetPasswordModal] = React.useState(false);

	React.useEffect(refreshPortalUser, [refreshPortalUser]);

	if (!user) return null;
	return (
		<PlayupThemeProvider>
			{showVerifyID && <GreenId onClose={() => setShowVerifyID(false)} onRefresh={refreshPortalUser} />}
			{resetPasswordModal && <ResetPasswordModal onClose={() => setResetPasswordModal(false)} />}
			<ProfileContainer isSmallScreen={isSmall}>
				<ProfileSection isSmallScreen={isSmall}>
					<UserInfoCard>
						<AccountStatus
							isVerified={!!user.id_verified}
							isSmallScreen={isSmall}
							user={user}
							refreshPortalUser={refreshPortalUser}
							notify={notify}
							onShowVerifyID={() => setShowVerifyID(true)}
							AvatarUploader={AvatarUploader}
						/>
						<FinancialButtonGroup>
							<UserBalance isSmallScreen={isSmall}>
								<p>Balance (AUD)</p>
								<BalanceAmount isSmallScreen={isSmall}>{formatCurrency(user?.balance)}</BalanceAmount>
							</UserBalance>
							<DepositButton isSmallScreen={isSmall} onClick={handleDeposit}>
								Deposit <IconDeposit />
							</DepositButton>
						</FinancialButtonGroup>
					</UserInfoCard>
				</ProfileSection>
				<ProfileSection isSmallScreen={isSmall}>
					<ProfileForm onResetPasswordButtonPress={() => setResetPasswordModal(true)} isSmallScreen={isSmall} />
				</ProfileSection>
			</ProfileContainer>
		</PlayupThemeProvider>
	);
};

export default PortalProfile;

export interface PortalProfileProps {
	isUS: boolean;
	smallScreen?: boolean;
	user: PortalUser | null;
	refreshPortalUser: () => void;
	notify: (options: any) => void;
	handleDeposit: () => void;
	GreenId: React.FC<{ onClose: () => void; onRefresh: () => void }>;
	ResetPasswordModal: React.FC<{
		onClose: () => void;
	}>;
	ProfileForm: React.FC<{
		isSmallScreen: boolean;
		onResetPasswordButtonPress: () => void;
	}>;
	AvatarUploader: React.FC<{
		user: PortalUser;
		isSmallScreen: boolean;
		refreshUser: () => void;
		notify: (options: any) => void;
	}>;
}

const ProfileContainer = styled.div<{ isSmallScreen }>`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	width: 100%;

	${({ isSmallScreen }) => isSmallScreen && 'flex-direction: column'};
`;

const ProfileSection = styled.div<{ isSmallScreen }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 20px;
	width: ${({ isSmallScreen }) => (isSmallScreen ? '100%' : '50%')};
`;

const UserInfoCard = styled.div`
	width: 100%;
	background-color: ${({ theme }) => theme.colors.darkGreyR};
	border-radius: 5px;
`;

const FinancialButtonGroup = styled.div`
	width: 95%;
	display: flex;
	margin: 0 auto;
`;

const UserBalance = styled.div<{ isSmallScreen }>`
	flex-basis: 47%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	height: ${({ isSmallScreen }) => (isSmallScreen ? '50px' : '60px')};
	margin: ${({ isSmallScreen }) => (isSmallScreen ? '20px 2%' : '20px 2%')};
	border-radius: 5px;
	background-color: ${({ theme }) => theme.colors.darkR};
	color: ${({ theme }) => theme.colors.greyR};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '11px' : '14px')};
`;

const BalanceAmount = styled.p<{ isSmallScreen }>`
	color: ${({ theme }) => theme.colors.white};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '16px' : '20px')};
	font-weight: 600;
`;

const DepositButton = styled(Button)<{ isSmallScreen }>`
	flex-basis: 47%;
	margin: ${({ isSmallScreen }) => (isSmallScreen ? '20px 2%' : '20px 2%')};
`;

const IconDeposit = styled(FaSignInAlt)`
	margin-left: 5px;
`;

const ResetPasswordButton = styled(Button)<{ isSmallScreen }>`
	width: 90%;
	margin: 20px auto;
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '16px' : '20px')};
`;
